import { Fragment, ReactElement } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import styled from 'styled-components'

import { StopIcon } from '@10xLabs/react-web/components/Icons/Stop'
import { Dropdown } from 'components/v2/Dropdown'
import List from 'components/v2/List'
import { StyledLink } from 'components/v2/Link'
import { FormattedPrice } from '@10xLabs/react-web/components/FormattedPrice'
import { FormattedDuration } from '@10xLabs/react-web/components/FormattedDuration'
import { FormattedTime } from '@10xLabs/react-web/components/FormattedTime'
import { Radio } from 'components/v2/Radio'
import { SeatsTemplateQuery } from '../SeatsTemplateQuery'
import { ITripList } from './types'

const StopsWrapper = styled.div<TSFixMe>`
  display: flex;
  align-items: flex-start;

  > svg {
    margin-right: 8px;
  }
`

const Place = styled.div<TSFixMe>`
  text-transform: capitalize;
`

const Table = styled.table<TSFixMe>`
  border-collapse: separate;
  width: 100%;
  border-spacing: 0px 8px;
`

const Th = styled.th<TSFixMe>`
  border-bottom: 2px solid rgba(102, 102, 102, 0.3);
  text-align: left;
  padding: 10px;
`

const Tr = styled.tr<{ isDisabled?: boolean }>`
  box-shadow: 0 2px 7px 0 rgba(0, 0, 0, 0.16);
  cursor: ${(props): string => (props.isDisabled ? 'not-allowed' : 'pointer')};
  opacity: ${(props): string => (props.isDisabled ? '0.3' : '1')};
`

const Td = styled.td<{ isActive?: boolean; isFirst?: boolean; isLast?: boolean }>`
  padding: 10px 10px 15px 10px;
  vertical-align: middle;

  ${(props): string => {
    if (props.isActive) {
      return `
        border-color: #207eff;
        border-style: solid;
        border-top-width: 2px;
        border-bottom-width: 2px;
        border-left-width: ${props.isFirst ? '2px' : 0};
        border-right-width: ${props.isLast ? '2px' : 0};
      `
    }

    return ``
  }}
`

const PriceIndicator = styled.div<TSFixMe>`
  display: inline-block;
  padding: 5px 15px;
  border-width: 1px;
  border-style: solid;
  border-radius: 3px;
`

const HigherPrice = styled(PriceIndicator)<TSFixMe>`
  color: #0076e0;
  border-color: #79c0ff;
  background-color: #eef6ff;
`

const LowerPrice = styled(PriceIndicator)<TSFixMe>`
  color: #419845;
  border-color: rgba(65, 152, 69, 0.3);
  background-color: #ebf6ec;
`

const SeatsTemplateWrapper = styled.div<TSFixMe>`
  background-color: rgba(246, 245, 247, 0.44);
  padding: 20px;
`

const compareFares = (oldBasePrice: number, newBasePrice: number): { old: number; new: number } => {
  return {
    old: oldBasePrice,
    new: newBasePrice
  }
}

export const TripsList = ({
  tripSegments,
  oldTickets,
  booking,
  oldTrip,
  onStartToReserveSeat,
  editPriceDisabled,
  ...props
}: ITripList): ReactElement => {
  const intl = useIntl()

  return (
    <Table>
      <thead>
        <tr>
          <Th />
          <Th>
            <FormattedMessage id="dictionary.departure" defaultMessage="Departure" />
          </Th>
          <Th>
            <FormattedMessage id="dictionary.arrival" defaultMessage="Arrival" />
          </Th>
          <Th>
            <FormattedMessage id="dictionary.duration" defaultMessage="Duration" />
          </Th>
          <Th>
            <FormattedMessage id="bookings.edit.trips.stops.label" defaultMessage="Stops" />
          </Th>
          {!editPriceDisabled && (
            <Th>
              <FormattedMessage id="dictionary.price" defaultMessage="Price" />
            </Th>
          )}
          <Th>
            <FormattedMessage id="dictionary.availableSeats" defaultMessage="Available seats" />
          </Th>
          {!editPriceDisabled && <Th />}
        </tr>
      </thead>
      <tbody>
        {tripSegments.map((tripSegment, index): ReactElement => {
          const firstStop = tripSegment.itinerary[0]
          const lastStop = tripSegment.itinerary[tripSegment.itinerary.length - 1]
          const intermediateStops = tripSegment.itinerary.slice(1, tripSegment.itinerary.length - 1)
          const comparedFares = compareFares(oldTrip.segment.fare?.basePrice || 0, tripSegment.basePrice)

          const handleSelectTripForMove = (): void => {
            props.setFieldValue('newTripSegment', tripSegment)

            const hasSelectedNewTripSegment =
              !props.values.newTripSegment || props.values.newTripSegment.id !== tripSegment.id
            if (hasSelectedNewTripSegment) {
              const isModifyingSameTripSegment =
                tripSegment.tripID === oldTrip.id && tripSegment.segment.id === oldTrip.segment.id
              if (isModifyingSameTripSegment) {
                props.setFieldValue(
                  'tickets',
                  oldTickets.map((ticket) => {
                    return {
                      ...ticket,
                      updatedCount: 1
                    }
                  })
                )
              } else {
                props.setFieldValue('tickets', [])
              }
            }
          }

          const selectTrip = async (): Promise<void> => {
            handleSelectTripForMove()
          }

          const isSelectedTripSegment = props.values.newTripSegment && props.values.newTripSegment.id === tripSegment.id
          const isSameTripSegment = tripSegment.tripID === oldTrip.id && tripSegment.segment.id === oldTrip.segment.id

          return (
            <Fragment key={index}>
              <Tr key={index} onClick={selectTrip}>
                <Td isActive={isSelectedTripSegment} isFirst>
                  <Radio
                    id="newTripSegment"
                    name={tripSegment.id}
                    checked={props.values.newTripSegment.id === tripSegment.id}
                    onChange={(): void => {
                      return
                    }}
                  />
                </Td>

                <Td isActive={isSelectedTripSegment}>
                  <FormattedTime intl={intl} time={firstStop.departureTime} noIcon />
                </Td>

                <Td isActive={isSelectedTripSegment}>
                  <FormattedTime intl={intl} time={lastStop.arrivalTime} noIcon />
                </Td>

                <Td isActive={isSelectedTripSegment}>
                  <FormattedDuration duration={tripSegment.duration} />
                </Td>

                <Td isActive={isSelectedTripSegment}>
                  {intermediateStops.length > 0 ? (
                    <StopsWrapper>
                      <StopIcon />

                      <Dropdown toggle={<StyledLink>{intermediateStops.length}</StyledLink>} noPadding>
                        <List.Container>
                          {intermediateStops.map(({ place }): TSFixMe => {
                            return (
                              <List.Item key={place.id}>
                                <Place>{place.name}</Place>
                              </List.Item>
                            )
                          })}
                        </List.Container>
                      </Dropdown>
                    </StopsWrapper>
                  ) : (
                    <FormattedMessage id="dictionary.direct" defaultMessage="Direct" />
                  )}
                </Td>

                {!editPriceDisabled && (
                  <Td isActive={isSelectedTripSegment}>
                    <FormattedPrice value={tripSegment.basePrice} />
                  </Td>
                )}
                <Td isActive={isSelectedTripSegment} isLast={editPriceDisabled}>
                  {tripSegment.availableSeatsNumber}
                </Td>
                {!editPriceDisabled && (
                  <Td isActive={isSelectedTripSegment} isLast>
                    {comparedFares.new > comparedFares.old && (
                      <HigherPrice>
                        <FormattedMessage id="bookings.edit.trips.higherPrice" defaultMessage="Higher price" />
                      </HigherPrice>
                    )}

                    {comparedFares.new < comparedFares.old && (
                      <LowerPrice>
                        <FormattedMessage id="bookings.edit.trips.lowerPrice" defaultMessage="Lower price" />
                      </LowerPrice>
                    )}
                  </Td>
                )}
              </Tr>
              {isSelectedTripSegment && (
                <tr>
                  <td colSpan={8}>
                    <SeatsTemplateWrapper>
                      <SeatsTemplateQuery
                        tripSegment={tripSegment}
                        booking={booking}
                        oldTickets={oldTickets}
                        onStartToReserveSeat={onStartToReserveSeat}
                        editPriceDisabled={editPriceDisabled}
                        isModifyingSameTripSegment={isSameTripSegment}
                        {...props}
                      />
                    </SeatsTemplateWrapper>
                  </td>
                </tr>
              )}
            </Fragment>
          )
        })}
      </tbody>
    </Table>
  )
}
